export type GreenProOperationalProfile = 'surebet' | 'trader' | 'hybrid';

export type GreenProAccountType = 'personal' | 'partnership';

export type GreenProOperationalCategory = 'sports' | 'arbitrage' | 'both';

export type GreenProOperationalFocus = 'arbitrage' | 'sports' | 'casino';

export interface GreenProOperationalHouseLike {
  name?: string | null;
  partnership_id?: string | null;
  partnership_name?: string | null;
  account_type?: string | null;
  operational_category?: string | null;
}

export interface GreenProOperationalSnapshot {
  profile: GreenProOperationalProfile;
  hasPersonalAccounts: boolean;
  hasPartnershipAccounts: boolean;
  sportsAccounts: number;
  arbitrageAccounts: number;
  bothAccounts: number;
}

export const GREENPRO_OPERATIONAL_PROFILE_OPTIONS: Array<{
  value: GreenProOperationalProfile;
  label: string;
  description: string;
}> = [
  {
    value: 'surebet',
    label: 'Arbitragem',
    description: 'Prioriza arbitragem com foco em operacao Surebet, parceiros e distribuicao de banca.',
  },
  {
    value: 'trader',
    label: 'Trader Esportivo',
    description: 'Prioriza apostas esportivas, operacao punter/trader e fluxo simplificado.',
  },
  {
    value: 'hybrid',
    label: 'Híbrido',
    description: 'Combina arbitragem, trader esportivo e cassino na mesma operacao.',
  },
];

function normalizeText(value?: string | null) {
  return (value || '').trim().toLowerCase();
}

export function normalizeGreenProAccountType(value?: string | null): GreenProAccountType | null {
  const normalized = normalizeText(value);

  if (normalized === 'personal' || normalized === 'pessoal') {
    return 'personal';
  }

  if (normalized === 'partnership' || normalized === 'parceria') {
    return 'partnership';
  }

  return null;
}

export function normalizeGreenProOperationalCategory(
  value?: string | null
): GreenProOperationalCategory | null {
  const normalized = normalizeText(value);

  if (normalized === 'sports' || normalized === 'sport' || normalized === 'esportiva') {
    return 'sports';
  }

  if (normalized === 'arbitrage' || normalized === 'surebet' || normalized === 'arbitragem') {
    return 'arbitrage';
  }

  if (normalized === 'both' || normalized === 'ambas' || normalized === 'all') {
    return 'both';
  }

  return null;
}

export function resolveGreenProHouseAccountType(
  house: Pick<GreenProOperationalHouseLike, 'account_type' | 'partnership_id' | 'partnership_name'>
): GreenProAccountType {
  const explicitType = normalizeGreenProAccountType(house.account_type);
  if (explicitType) {
    return explicitType;
  }

  return normalizeText(house.partnership_id || house.partnership_name) ? 'partnership' : 'personal';
}

export function resolveGreenProOperationalCategory(
  house: Pick<GreenProOperationalHouseLike, 'operational_category'>,
  fallback: GreenProOperationalCategory = 'both'
): GreenProOperationalCategory {
  return normalizeGreenProOperationalCategory(house.operational_category) || fallback;
}

export function isHouseCompatibleWithOperationalFocus(
  house: Pick<GreenProOperationalHouseLike, 'operational_category'>,
  focus: GreenProOperationalFocus
) {
  if (focus === 'casino') {
    return true;
  }

  const category = resolveGreenProOperationalCategory(house, focus === 'arbitrage' ? 'arbitrage' : 'sports');

  if (focus === 'arbitrage') {
    return category === 'arbitrage' || category === 'both';
  }

  return category === 'sports' || category === 'both';
}

export function isHouseCompatibleWithBankAccount(params: {
  bankPartnership?: string | null;
  bankAccountType?: string | null;
  housePartnership?: string | null;
  houseAccountType?: string | null;
}) {
  const bankAccountType = normalizeGreenProAccountType(params.bankAccountType);
  const houseAccountType = resolveGreenProHouseAccountType({
    account_type: params.houseAccountType,
    partnership_name: params.housePartnership,
  });

  if (bankAccountType === 'personal') {
    return true;
  }

  if (houseAccountType === 'personal') {
    return true;
  }

  const normalizedBankPartnership = normalizeText(params.bankPartnership);
  const normalizedHousePartnership = normalizeText(params.housePartnership);

  return Boolean(normalizedBankPartnership) && normalizedBankPartnership === normalizedHousePartnership;
}

export function inferGreenProOperationalSnapshot(
  houses: Array<Pick<GreenProOperationalHouseLike, 'partnership_id' | 'partnership_name' | 'account_type' | 'operational_category'>>,
  options?: {
    sportsBetCount?: number;
    surebetCount?: number;
    casinoBetCount?: number;
  }
): GreenProOperationalSnapshot {
  let sportsAccounts = 0;
  let arbitrageAccounts = 0;
  let bothAccounts = 0;
  let hasPersonalAccounts = false;
  let hasPartnershipAccounts = false;

  for (const house of houses) {
    const accountType = resolveGreenProHouseAccountType(house);
    const category = resolveGreenProOperationalCategory(house);

    if (accountType === 'personal') {
      hasPersonalAccounts = true;
    } else {
      hasPartnershipAccounts = true;
    }

    if (category === 'sports') {
      sportsAccounts += 1;
    } else if (category === 'arbitrage') {
      arbitrageAccounts += 1;
    } else {
      bothAccounts += 1;
    }
  }

  const sportsLoad = (options?.sportsBetCount || 0) + (options?.surebetCount || 0);
  const casinoLoad = options?.casinoBetCount || 0;

  let profile: GreenProOperationalProfile = 'surebet';

  if ((sportsAccounts > 0 && arbitrageAccounts > 0) || bothAccounts > 0 || (sportsLoad > 0 && casinoLoad > 0)) {
    profile = 'hybrid';
  } else if (hasPersonalAccounts && !hasPartnershipAccounts) {
    profile = casinoLoad > 0 && sportsLoad === 0 ? 'hybrid' : 'trader';
  } else if (sportsAccounts > 0 && arbitrageAccounts === 0) {
    profile = 'trader';
  } else if (sportsLoad > 0 && casinoLoad > 0) {
    profile = 'hybrid';
  }

  return {
    profile,
    hasPersonalAccounts,
    hasPartnershipAccounts,
    sportsAccounts,
    arbitrageAccounts,
    bothAccounts,
  };
}

export function isPathVisibleForOperationalProfile(
  profile: GreenProOperationalProfile,
  path: string
) {
  if (profile === 'hybrid') {
    return true;
  }

  const hiddenPathsByProfile: Record<Exclude<GreenProOperationalProfile, 'hybrid'>, string[]> = {
    trader: ['/dashboard-opportunities', '/surebets', '/promotions', '/partnerships', '/partner-dashboard', '/card-rules'],
    surebet: ['/sports-bets'],
  };

  return !hiddenPathsByProfile[profile].includes(path);
}

export function getOperationalNavigationWeight(profile: GreenProOperationalProfile, path: string) {
  const weights: Record<GreenProOperationalProfile, Record<string, number>> = {
    surebet: {
      '/dashboard-opportunities': 0,
      '/surebets': 1,
      '/promotions': 2,
      '/betting-house-management': 3,
      '/partnerships': 4,
      '/sports-bets': 5,
      '/casino-bets': 6,
      '/bank-accounts': 7,
      '/transactions': 8,
      '/reports': 9,
    },
    trader: {
      '/sports-bets': 0,
      '/reports': 1,
      '/bank-accounts': 2,
      '/transactions': 3,
      '/betting-house-management': 4,
      '/surebets': 5,
      '/promotions': 6,
      '/dashboard-opportunities': 7,
      '/partnerships': 8,
      '/casino-bets': 9,
    },
    hybrid: {
      '/sports-bets': 0,
      '/casino-bets': 1,
      '/surebets': 2,
      '/promotions': 3,
      '/dashboard-opportunities': 4,
      '/betting-house-management': 5,
      '/bank-accounts': 6,
      '/transactions': 7,
      '/reports': 8,
      '/partnerships': 9,
    },
  };

  return weights[profile][path] ?? 999;
}